/**
 * Popup выбора WABA шаблона по триггеру `/` (TODO-027a)
 * Дата: 15.01.2026
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/hooks/useWABATemplates.ts` — источник templates
 * - `frontend/src/components/features/messages/ChatWindow.tsx` — управляет показом popup и строкой поиска
 *
 * КАК РАБОТАЕТ (кратко):
 * - Показывает список approved WABA templates, фильтрует по query (после `/`)
 * - По клику возвращает выбранный template
 *
 * СХЕМА:
 * - Композиция функций:
 *   - WABATemplatePicker -> filterTemplates -> onSelect(template)
 */
import { useMemo } from 'react';
import { X, Search } from 'lucide-react';
import type { WABATemplate } from '@/api/templates';
import { cn } from '@/utils/cn';

interface WABATemplatePickerProps {
  isOpen: boolean;
  templates: WABATemplate[];
  query: string; // текст после "/"
  onQueryChange: (q: string) => void;
  onSelect: (t: WABATemplate) => void;
  onClose: () => void;
}

export function WABATemplatePicker({ isOpen, templates, query, onQueryChange, onSelect, onClose }: WABATemplatePickerProps) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const name = (t.name || '').toLowerCase();
      const title = (t.title || '').toLowerCase();
      return name.includes(q) || title.includes(q);
    });
  }, [query, templates]);

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'absolute bottom-[72px] left-4 right-4 z-40',
        'rounded-lg border border-neutral-200 bg-white shadow-soft'
      )}
      data-testid="waba-template-picker"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <div className="text-sm font-semibold text-neutral-900">Шаблоны WABA</div>
        <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 transition-colors" aria-label="Close">
          <X className="w-4 h-4 text-neutral-500" />
        </button>
      </div>

      <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-2">
        <Search className="w-4 h-4 text-neutral-400" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Поиск…"
          className="w-full text-sm outline-none"
          data-testid="waba-template-picker-search"
        />
      </div>

      <div className="max-h-72 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-4 py-6 text-sm text-neutral-500">Шаблоны не найдены</div>
        ) : (
          filtered.map((t) => (
            <button
              key={t.template_guid || t.id || t.name}
              className="w-full text-left px-4 py-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
              onClick={() => onSelect(t)}
              data-testid={`waba-template-item-${t.name}`}
            >
              <div className="text-sm font-medium text-neutral-900">{t.name}</div>
              {t.title && <div className="text-xs text-neutral-600 mt-0.5">{t.title}</div>}
              {t.body_text && (
                <div className="text-xs text-neutral-500 mt-1 line-clamp-2">
                  {t.body_text}
                </div>
              )}
              {t.variables?.length ? (
                <div className="text-xs text-neutral-500 mt-1">
                  Переменные: {t.variables.map((v) => `{{${v}}}`).join(', ')}
                </div>
              ) : null}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

